import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { planOf, planOfShop } from '../constants/plans';

// ── PROFILE ANNOUNCEMENTS ────────────────────────────────────────────────────
// The banner a silver or gold designer pins above their profile: one still for
// silver, up to three as a carousel for gold.
//
// ITS OWN DOCUMENT, NOT A FIELD ON users/{uid}. That document is read in BULK —
// fetchDesigners pulls every designer for the directory, the search page and the
// feed — so three base64 images on it would be downloaded by everyone who opens
// /designers, for every designer in the list, to render a page that shows none
// of them. Here they cost exactly one read, and only when someone opens the
// profile they belong to.
//
// One doc per designer, keyed by uid, so there is no id to look up and no query
// to index: the profile already knows whose page it is.
const COL = 'announcements';

// Same ceiling the gallery uses for a full-size image (see PHOTO_FULL_MAX_BYTES
// in constants/gallery.js). A banner is displayed wider than a gallery tile, so
// it gets the full budget rather than the thumbnail one — but three of them plus
// the document overhead must still clear Firestore's 1MiB limit, which is why
// the cap is per image and the count is capped too.
export const ANNOUNCEMENT_MAX_CHARS = 300 * 1024;

/**
 * How many banners this account may publish. 0 means the feature is not theirs.
 *
 * WHICH LADDER IS AN ARGUMENT, not a guess. A shop and a designer both store
 * their tier in `plan`, and 'silver' means one banner on either — but the
 * ladders are separate tables and a shop has no bronze, so reading the designer
 * one for a shop would be right only by coincidence. Passing it in makes the
 * caller say which, and there is no document shape to sniff.
 */
export function announcementLimit(user, isShop = false) {
  return (isShop ? planOfShop(user) : planOf(user)).announcements || 0;
}

/**
 * The banners for one designer, oldest first.
 *
 * FAILS SOFT — a missing document, a rules refusal or a dropped connection all
 * return an empty list. This is decoration above a profile; it must never be
 * the reason the profile itself fails to render.
 */
export async function fetchAnnouncements(uid) {
  if (!uid) return [];
  try {
    const snap = await getDoc(doc(db, COL, uid));
    if (!snap.exists()) return [];
    const images = snap.get('images');
    return Array.isArray(images) ? images.filter(i => i && typeof i.url === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Replace the whole set.
 *
 * WHOLE-SET, not append: the editor holds the list the designer can see and
 * writes what it shows, so there is no way for the stored order to drift from
 * the order on screen, and removing one is the same operation as adding one.
 *
 * The count is trimmed to the caller's plan here AND enforced in
 * firestore.rules — this is the convenience, the rule is the guarantee. A
 * downgrade therefore cannot leave three banners live: the next write trims
 * them, and until then the profile only renders as many as the plan allows.
 */
export async function saveAnnouncements(uid, plan, images, isShop = false) {
  const limit = announcementLimit(plan, isShop);
  const clean = (images || [])
    .filter(i => i && typeof i.url === 'string' && i.url.length <= ANNOUNCEMENT_MAX_CHARS)
    .slice(0, limit)
    .map(i => ({
      url: i.url,
      // Kept so the carousel can reserve the right box before the image
      // decodes, which is what stops the page jumping as it loads.
      w: Number(i.w) || 0,
      h: Number(i.h) || 0,
      ...(i.link ? { link: String(i.link).slice(0, 300) } : {}),
    }));

  // Nothing left to show — remove the document rather than leaving an empty
  // array behind, so a reader's `exists()` check is the honest answer.
  if (!clean.length) {
    await deleteDoc(doc(db, COL, uid)).catch(() => {});
    return [];
  }

  await setDoc(doc(db, COL, uid), { images: clean, updatedAt: serverTimestamp() });
  return clean;
}
